/**
 * Gestes clavier partagés des surfaces modales.
 */
import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLES = [
  "a[href]", "button:not([disabled])", "textarea:not([disabled])",
  "input:not([disabled])", "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Une surface modale complète : Échap ferme, le focus est **piégé** dedans, et
 * il **revient** d'où il venait à la fermeture.
 *
 * `aria-modal="true"` promet ce confinement ; sans lui, la tabulation sortait
 * de la boîte vers la page dessous, et le lecteur d'écran s'y perdait. Le hook
 * rend une ref à poser sur le conteneur de la modale ; il n'agit que tant que
 * `actif`. On garde `fermer` dans une ref pour que l'abonnement ne se refasse
 * qu'à l'ouverture et à la fermeture.
 */
export function useModale<T extends HTMLElement = HTMLDivElement>(
  actif: boolean, fermer: () => void,
): RefObject<T | null> {
  const ref = useRef<T>(null);
  const fermerRef = useRef(fermer);
  fermerRef.current = fermer;

  useEffect(() => {
    if (!actif) return undefined;
    const boite = ref.current;
    const precedent = document.activeElement as HTMLElement | null;

    const focusables = (): HTMLElement[] => boite
      ? Array.from(boite.querySelectorAll<HTMLElement>(FOCUSABLES))
          .filter((el) => el.offsetParent !== null)
      : [];

    // Le focus entre dans la boîte : le premier geste, ou la boîte elle-même.
    const premiers = focusables();
    (premiers[0] ?? boite)?.focus();

    const surTouche = (evenement: KeyboardEvent) => {
      if (evenement.key === "Escape") {
        evenement.preventDefault();
        fermerRef.current();
        return;
      }
      if (evenement.key !== "Tab" || !boite) return;
      const liste = focusables();
      if (liste.length === 0) { evenement.preventDefault(); return; }
      const premier = liste[0]!;
      const dernier = liste[liste.length - 1]!;
      const courant = document.activeElement;
      // Aux deux bords, on boucle ; hors de la boîte, on ramène dedans.
      if (evenement.shiftKey && courant === premier) {
        evenement.preventDefault();
        dernier.focus();
      } else if (!evenement.shiftKey && courant === dernier) {
        evenement.preventDefault();
        premier.focus();
      } else if (courant instanceof Node && !boite.contains(courant)) {
        evenement.preventDefault();
        premier.focus();
      }
    };

    document.addEventListener("keydown", surTouche);
    return () => {
      document.removeEventListener("keydown", surTouche);
      // Le focus revient à sa place — la ligne de la liste, le bouton d'où
      // l'on venait — au lieu d'être renvoyé en haut de la page.
      precedent?.focus?.();
    };
  }, [actif]);

  return ref;
}

/**
 * Échap ferme, tant que `actif`.
 *
 * L'écouteur est posé sur le **document**, non sur la boîte : sans piège de
 * focus, une touche frappée hors de la modale n'atteindrait pas un `onKeyDown`
 * local, et le réflexe d'Échap doit marcher où qu'aille le focus. `fermer` est
 * tenu par une ref pour que l'abonnement ne se refasse qu'à l'ouverture et à
 * la fermeture, non à chaque rendu.
 */
export function useEchap(actif: boolean, fermer: () => void): void {
  const fermerRef = useRef(fermer);
  fermerRef.current = fermer;

  useEffect(() => {
    if (!actif) return undefined;
    const surTouche = (evenement: KeyboardEvent) => {
      if (evenement.key === "Escape") {
        evenement.preventDefault();
        fermerRef.current();
      }
    };
    document.addEventListener("keydown", surTouche);
    return () => document.removeEventListener("keydown", surTouche);
  }, [actif]);
}
